import { useEffect, useState, useMemo, useCallback, Fragment } from "react";
import { api, idr, fmtNum } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import {
  TrendingUp, RefreshCw, Filter, Trash2, Plus, X, Users, Target, Clock, AlertTriangle,
  ChevronDown, ChevronUp, LineChart as LineChartIcon, BarChart2, ClipboardList,
  LayoutTemplate, Sparkles, Wand2, FilePlus2, Pencil, Save, Download,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Cell,
} from "recharts";
import { MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { bulanIniISO } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

const catColor = {
  "Sangat Baik": "bg-emerald-100 text-emerald-800",
  "Baik": "bg-sky-100 text-sky-800",
  "Perlu Perhatian": "bg-amber-100 text-amber-800",
  "Kritis": "bg-rose-100 text-rose-800",
};

// Helpers: month arithmetic
const monthAdd = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  let ny = y, nm = m + delta;
  while (nm <= 0) { nm += 12; ny -= 1; }
  while (nm > 12) { nm -= 12; ny += 1; }
  return `${ny}-${String(nm).padStart(2, "0")}`;
};
const currentMonth = () => bulanIniISO();

export default function Performance() {
  const { user, can } = useAuth();
  const canCreate = can("performance", "create");
  const canDelete = can("performance", "delete");
  const [periodStart, setPeriodStart] = useState(monthAdd(currentMonth(), -5));
  const [periodEnd, setPeriodEnd] = useState(currentMonth());
  const [selectedEmp, setSelectedEmp] = useState(""); // "" = all
  const [monthsBack, setMonthsBack] = useState(6);
  const [auto, setAuto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // employee_id expanded row
  // Manual entry / custom KPI modal
  const [manualModal, setManualModal] = useState(null);
  const [kpiTemplates, setKpiTemplates] = useState([]);
  const [selectedKpiTpl, setSelectedKpiTpl] = useState("");
  const [aiCoaching, setAiCoaching] = useState(false);
  // Custom KPI Template CRUD modal (superadmin)
  const [kpiTplModal, setKpiTplModal] = useState(null);  // { mode, key, label, kpis, _origKey }
  const [kpiTplSaving, setKpiTplSaving] = useState(false);
  const isSuperadmin = user?.role === "superadmin";
  const customKpiTemplates = useMemo(() => kpiTemplates.filter(t => t.is_custom), [kpiTemplates]);
  const reloadKpiTemplates = () => api.get("/performance/kpi-templates").then(r => setKpiTemplates(r.data?.templates || []));
  // For quick presets
  const setPeriodMonths = (n) => {
    setMonthsBack(n);
    setPeriodStart(monthAdd(currentMonth(), -(n - 1)));
    setPeriodEnd(currentMonth());
  };

  const fetchAuto = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    p.set("periode_start", periodStart);
    p.set("periode_end", periodEnd);
    if (selectedEmp) p.set("employee_id", selectedEmp);
    api.get(`/performance/auto?${p}`)
      .then(r => setAuto(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal load"))
      .finally(() => setLoading(false));
  }, [periodStart, periodEnd, selectedEmp]);

  useEffect(() => { fetchAuto(); }, [fetchAuto]);

  const [emps, setEmps] = useState([]);
  useEffect(() => {
    api.get("/employees").then(r => setEmps(r.data));
    api.get("/performance/kpi-templates").then(r => setKpiTemplates(r.data?.templates || []));
  }, []);

  const summary = auto?.summary || {};
  const results = useMemo(() => auto?.results || [], [auto]);
  const months = useMemo(() => auto?.months || [], [auto]);

  // Combined line chart data: sum target + realisasi per month across all shown employees
  const chartData = useMemo(() => months.map(m => {
    let tgt = 0, rea = 0, late = 0, alpa = 0;
    results.forEach(r => {
      const mm = r.monthly.find(x => x.periode === m);
      if (mm) { tgt += mm.target_omzet; rea += mm.realisasi_omzet; late += mm.late_days; alpa += mm.alpa_days; }
    });
    return { periode: m, target: Math.round(tgt), realisasi: Math.round(rea), late, alpa };
  }), [months, results]);

  // Discipline chart per employee (last month of range)
  const disciplineData = useMemo(() => results.slice(0, 15).map(r => {
    const totalLate = r.monthly.reduce((a, m) => a + m.late_days, 0);
    const totalAlpa = r.monthly.reduce((a, m) => a + m.alpa_days, 0);
    return { nama: r.nama.length > 14 ? r.nama.slice(0, 14) + "…" : r.nama, late: totalLate, alpa: totalAlpa };
  }), [results]);

  const openManual = (r, periode) => {
    const monthlyRow = r.monthly.find(x => x.periode === periode) || {};
    setManualModal({
      employee_id: r.employee_id, employee_nama: r.nama,
      periode,
      target_omzet: monthlyRow.target_omzet || 0,
      realisasi_omzet: monthlyRow.realisasi_omzet || 0,
      target_unit: (r.poin_target || 0),
      realisasi_unit: (monthlyRow.unit_hp || 0) + (monthlyRow.unit_acc || 0),
      kesimpulan: monthlyRow.manual_kesimpulan || "",
      kelemahan: monthlyRow.manual_kelemahan || "",
      custom_kpis: monthlyRow.custom_kpis || [],
    });
  };
  const saveManual = async () => {
    try {
      await api.post("/performance", manualModal);
      toast.success("Catatan performa tersimpan");
      setManualModal(null);
      fetchAuto();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const addCustomKPI = () => {
    setManualModal(m => ({ ...m, custom_kpis: [...(m.custom_kpis || []), { name: "", target: 0, realisasi: 0 }] }));
  };
  const updCustomKPI = (i, k, v) => {
    setManualModal(m => {
      const arr = [...(m.custom_kpis || [])];
      arr[i] = { ...arr[i], [k]: k === "name" ? v : (parseFloat(v) || 0) };
      return { ...m, custom_kpis: arr };
    });
  };
  const rmCustomKPI = (i) => {
    setManualModal(m => ({ ...m, custom_kpis: (m.custom_kpis || []).filter((_, j) => j !== i) }));
  };

  const applyKpiTemplate = (key) => {
    setSelectedKpiTpl(key);
    if (!key || !manualModal) return;
    const tpl = kpiTemplates.find(t => t.key === key);
    if (!tpl) return;
    setManualModal(m => ({ ...m, custom_kpis: [...(m.custom_kpis || []), ...tpl.kpis.map(k => ({ ...k }))] }));
    toast.success(`+${tpl.kpis.length} KPI dari "${tpl.label}" ditambahkan`);
  };

  // ---- Custom KPI Template CRUD ops ----
  const openNewKpiTpl = () => setKpiTplModal({ mode: "new", key: "", label: "", kpis: [{ name: "", target: 0, realisasi: 0 }] });
  const openEditKpiTpl = (tpl) => {
    const rawKey = (tpl.key || "").replace(/^custom_/, "");
    setKpiTplModal({ mode: "edit", key: rawKey, label: tpl.label || "",
      kpis: (tpl.kpis || []).map(k => ({ name: k.name || "", target: k.target ?? 0, realisasi: k.realisasi ?? 0 })),
      _origKey: tpl.key });
  };
  const tplAddKpi = () => setKpiTplModal(m => m ? ({ ...m, kpis: [...(m.kpis || []), { name: "", target: 0, realisasi: 0 }] }) : m);
  const tplUpdKpi = (i, k, v) => {
    setKpiTplModal(m => {
      if (!m) return m;
      const arr = [...(m.kpis || [])];
      arr[i] = { ...arr[i], [k]: k === "name" ? v : (parseFloat(v) || 0) };
      return { ...m, kpis: arr };
    });
  };
  const tplRmKpi = (i) => setKpiTplModal(m => m ? ({ ...m, kpis: (m.kpis || []).filter((_, j) => j !== i) }) : m);
  const saveKpiTpl = async () => {
    if (!kpiTplModal) return;
    if (!kpiTplModal.label.trim()) { toast.error("Label wajib diisi"); return; }
    if (!kpiTplModal.key.trim()) { toast.error("Key wajib diisi"); return; }
    const kpis = (kpiTplModal.kpis || []).filter(k => k.name && k.name.trim());
    if (kpis.length === 0) { toast.error("Minimal 1 KPI"); return; }
    setKpiTplSaving(true);
    try {
      await api.post("/performance/kpi-templates/custom", {
        key: kpiTplModal.key.trim(),
        label: kpiTplModal.label.trim(),
        kpis: kpis.map(k => ({ name: k.name.trim(), target: parseFloat(k.target) || 0, realisasi: 0 })),
      });
      toast.success(kpiTplModal.mode === "edit" ? "Template diperbarui" : "Template KPI dibuat");
      await reloadKpiTemplates();
      setKpiTplModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setKpiTplSaving(false); }
  };
  const delKpiTpl = async (tpl) => {
    if (!confirm(`Hapus template "${tpl.label}"?`)) return;
    try {
      await api.delete(`/performance/kpi-templates/custom/${tpl.key}`);
      toast.success("Template dihapus");
      await reloadKpiTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const generateCoachingAI = async () => {
    if (!manualModal) return;
    setAiCoaching(true);
    try {
      const { data } = await api.post("/ai/coaching-suggest", {
        employee_id: manualModal.employee_id,
        periode: manualModal.periode,
      });
      setManualModal(m => ({
        ...m,
        kesimpulan: data.kesimpulan || m.kesimpulan,
        kelemahan: data.kelemahan || m.kelemahan,
      }));
      toast.success("AI coaching notes generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal generate coaching");
    } finally { setAiCoaching(false); }
  };

  // Kedua modal (catatan performa & template KPI) ada di JSX halaman yang
  // tidak unmount — `active` mengikuti state buka masing-masing supaya
  // dirty-state di-reset tiap kali dibuka lagi. Catatan kesimpulan/coaching
  // yang diketik manual (atau digenerate AI lalu diedit) mahal diulang.
  const manualGuard = useModalGuard({
    active: !!manualModal, dangerous: true,
    onClose: () => setManualModal(null), onSubmit: saveManual,
  });
  const kpiTplGuard = useModalGuard({
    active: !!kpiTplModal, dangerous: true, busy: kpiTplSaving,
    onClose: () => setKpiTplModal(null), onSubmit: saveKpiTpl,
  });

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Performa · Auto Report</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Laporan Performa Sales</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Target &amp; realisasi <b>otomatis</b> dari settings karyawan + input Penjualan. Kedisiplinan (telat &amp; alpa) ditarik dari Absensi. Bisa tracking sampai 12 bulan.
          </p>
        </div>
        <button onClick={fetchAuto} data-testid="btn-refresh-perf"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button onClick={async () => {
          const token = localStorage.getItem("hris_token");
          const p = new URLSearchParams();
          p.set("periode_start", periodStart);
          p.set("periode_end", periodEnd);
          if (selectedEmp) p.set("employee_id", selectedEmp);
          const base = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${base}/api/export/performance?${p}`, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) { toast.error("Gagal export"); return; }
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `Performance_${periodStart}_${periodEnd}.xlsx`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          toast.success("Export Excel diunduh");
        }} data-testid="btn-export-perf"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium">
          <Download className="w-4 h-4" /> Export Excel
        </button>
        {isSuperadmin && (
          <button onClick={openNewKpiTpl} data-testid="btn-manage-kpi-templates"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-violet-800 text-sm font-medium">
            <LayoutTemplate className="w-4 h-4" /> Kelola Template KPI
            {customKpiTemplates.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold">
                {customKpiTemplates.length}
              </span>
            )}
          </button>
        )}
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-5 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[280px] flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-semibold text-slate-600 mr-2">Preset:</span>
          {[1, 3, 6, 12].map(n => (
            <button key={n} onClick={() => setPeriodMonths(n)} data-testid={`btn-preset-${n}`}
              className={`h-9 px-3 rounded-lg text-xs font-medium ${monthsBack === n
                ? "bg-emerald-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
              {n} bulan
            </button>
          ))}
        </div>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari</span>
          <MonthPicker value={periodStart} onChange={setPeriodStart}
            data-testid="perf-start" max={periodEnd}
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai</span>
          <MonthPicker value={periodEnd} onChange={setPeriodEnd}
            data-testid="perf-end" min={periodStart} max={currentMonth()}
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Karyawan</span>
          <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}
            data-testid="perf-emp"
            className="mt-1 h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            <option value="">— Semua Karyawan —</option>
            {emps.map(e => <option key={e.id} value={e.id}>{e.nama}</option>)}
          </select>
        </label>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
        <SumCard icon={Users} label="Karyawan" value={fmtNum(summary.employee_count || 0)}
          sub={`${months.length} bulan`} tint="slate" tid="kpi-emp" />
        <SumCard icon={Target} label="Total Target" value={idr(summary.grand_target || 0)}
          sub="dari settings" tint="emerald" tid="kpi-target" />
        <SumCard icon={TrendingUp} label="Total Realisasi" value={idr(summary.grand_realisasi || 0)}
          sub={`${summary.grand_pct || 0}% pencapaian`} tint={((summary.grand_pct || 0) >= 100) ? "emerald" : (summary.grand_pct || 0) >= 75 ? "amber" : "rose"}
          tid="kpi-realisasi" />
        <SumCard icon={AlertTriangle} label="Kedisiplinan"
          value={`${disciplineData.reduce((a, d) => a + d.late, 0)} telat · ${disciplineData.reduce((a, d) => a + d.alpa, 0)} alpa`}
          sub="total (hari)" tint="rose" tid="kpi-discipline" />
      </div>

      {/* Line chart target vs realisasi */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base flex items-center gap-2">
              <LineChartIcon className="w-4 h-4 text-emerald-600" /> Target vs Realisasi ({months.length} bulan)
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {selectedEmp ? emps.find(e => e.id === selectedEmp)?.nama : "Total seluruh karyawan"} · {periodStart} → {periodEnd}
            </p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="periode" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(0)}jt` : v} />
            <Tooltip formatter={(v, k) => (k === "target" || k === "realisasi") ? idr(v) : v}
              contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="target" name="Target" fill="#94A3B8" radius={[4, 4, 0, 0]} />
            <Bar dataKey="realisasi" name="Realisasi" fill="#059669" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="late" name="Telat (hari)" stroke="#F59E0B" strokeWidth={2} yAxisId={0} />
            <Line type="monotone" dataKey="alpa" name="Alpa (hari)" stroke="#EF4444" strokeWidth={2} yAxisId={0} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Discipline chart */}
      {disciplineData.length > 0 && !selectedEmp && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-5">
          <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base flex items-center gap-2 mb-3">
            <BarChart2 className="w-4 h-4 text-rose-600" /> Kedisiplinan Karyawan · Total {months.length} Bulan
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(240, disciplineData.length * 26)}>
            <BarChart data={disciplineData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis dataKey="nama" type="category" tick={{ fontSize: 10 }} width={110} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="late" name="Telat" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              <Bar dataKey="alpa" name="Alpa" fill="#EF4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Employee table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="perf-table">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="text-left py-3 px-4">Karyawan</th>
              <th className="text-right py-3 px-4">Target</th>
              <th className="text-right py-3 px-4">Realisasi</th>
              <th className="text-center py-3 px-4">%</th>
              <th className="text-center py-3 px-4">
                <Clock className="w-3 h-3 inline mb-0.5 text-amber-600" /> Telat
              </th>
              <th className="text-center py-3 px-4">
                <AlertTriangle className="w-3 h-3 inline mb-0.5 text-rose-600" /> Alpa
              </th>
              <th className="text-right py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            <LoadingState
              loading={loading}
              kosong={!loading && results.length === 0}
              varian="tabel"
              kolom={7}
              pesanKosong="Belum ada data untuk periode ini"
              data-testid="performance-loading"
            />
            {results.map(r => {
              const isOpen = expanded === r.employee_id;
              const pct = r.total.pct;
              return (
                <Fragment key={r.employee_id}>
                  <tr data-testid={`perf-row-${r.employee_id}`}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpanded(isOpen ? null : r.employee_id)}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                        <div>
                          <p className="font-medium text-slate-900">{r.nama}</p>
                          <p className="text-[11px] text-slate-500">{r.branch || "-"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-xs">{idr(r.total.target)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs font-semibold text-emerald-700">{idr(r.total.omzet)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        pct >= 100 ? "bg-emerald-100 text-emerald-800"
                        : pct >= 80 ? "bg-sky-100 text-sky-800"
                        : pct >= 60 ? "bg-amber-100 text-amber-800"
                        : "bg-rose-100 text-rose-800"
                      }`}>{pct}%</span>
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-xs">{r.total.late_days}</td>
                    <td className="py-3 px-4 text-center font-mono text-xs">{r.total.alpa_days}</td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-[10px] text-emerald-700 italic">klik untuk detail bulanan</span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={7} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Monthly detail */}
                          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-slate-100 text-[10px] uppercase font-bold text-slate-500">
                                  <th className="text-left py-1.5 px-2">Bulan</th>
                                  <th className="text-right py-1.5 px-2">Realisasi</th>
                                  <th className="text-center py-1.5 px-2">%</th>
                                  <th className="text-center py-1.5 px-2">Telat</th>
                                  <th className="text-center py-1.5 px-2">Alpa</th>
                                  {canCreate && <th className="text-right py-1.5 px-2"></th>}
                                </tr>
                              </thead>
                              <tbody>
                                {r.monthly.map(m => (
                                  <tr key={m.periode} className="border-t border-slate-100">
                                    <td className="py-1.5 px-2 font-mono">{m.periode}</td>
                                    <td className="py-1.5 px-2 text-right font-mono">{idr(m.realisasi_omzet)}</td>
                                    <td className="py-1.5 px-2 text-center">
                                      <span className={`px-1.5 py-0 rounded text-[10px] ${catColor[m.kategori] || "bg-slate-100"}`}>
                                        {m.pct_achievement}%
                                      </span>
                                    </td>
                                    <td className="py-1.5 px-2 text-center">{m.late_days}</td>
                                    <td className="py-1.5 px-2 text-center">{m.alpa_days}</td>
                                    {canCreate && (
                                      <td className="py-1.5 px-2 text-right">
                                        <button onClick={(e) => { e.stopPropagation(); openManual(r, m.periode); }}
                                          data-testid={`btn-manual-${r.employee_id}-${m.periode}`}
                                          className="text-[10px] px-2 py-0.5 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 font-medium">
                                          Catatan
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {/* Custom KPIs preview */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                              <ClipboardList className="w-3.5 h-3.5" /> Variabel Custom (per bulan)
                            </p>
                            <div className="space-y-2 max-h-[280px] overflow-y-auto">
                              {r.monthly.filter(m => (m.custom_kpis || []).length > 0 || m.manual_kesimpulan).map(m => (
                                <div key={m.periode} className="bg-white border border-slate-200 rounded-lg p-2 text-[11px]">
                                  <p className="font-mono text-slate-500 mb-0.5">{m.periode}</p>
                                  {(m.custom_kpis || []).map((k, i) => (
                                    <div key={i} className="flex justify-between text-slate-700">
                                      <span>· {k.name}</span>
                                      <span className="font-mono">
                                        {k.realisasi}/{k.target} {k.target > 0 && <span className="text-slate-400">({Math.round((k.realisasi/k.target)*100)}%)</span>}
                                      </span>
                                    </div>
                                  ))}
                                  {m.manual_kesimpulan && <p className="text-slate-600 italic mt-1">💬 {m.manual_kesimpulan.slice(0, 100)}</p>}
                                </div>
                              ))}
                              {r.monthly.every(m => !m.custom_kpis?.length && !m.manual_kesimpulan) && (
                                <p className="text-[11px] text-slate-400 italic text-center py-2">Belum ada catatan/variabel custom — klik <b>Catatan</b> di baris bulan mana pun untuk menambahkan.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Manual/Custom KPI Modal */}
      {manualModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={manualGuard.requestClose}>
          <div ref={manualGuard.contentRef} className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="perf-manual-modal">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div>
                <h3 className="font-display font-semibold text-sm">Catatan Performa &amp; Variabel Custom</h3>
                <p className="text-[11px] text-emerald-100">{manualModal.employee_nama} · <span className="font-mono">{manualModal.periode}</span></p>
              </div>
              <button onClick={manualGuard.requestClose}
                className="p-1.5 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-[11px] text-emerald-800 mb-1">Auto-fill dari sistem:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Target Omzet:</span> <b className="font-mono">{idr(manualModal.target_omzet)}</b></div>
                  <div><span className="text-slate-500">Realisasi:</span> <b className="font-mono">{idr(manualModal.realisasi_omzet)}</b></div>
                  <div><span className="text-slate-500">Target Poin/Unit:</span> <b>{manualModal.target_unit}</b></div>
                  <div><span className="text-slate-500">Realisasi Unit:</span> <b>{manualModal.realisasi_unit}</b></div>
                </div>
              </div>

              {/* Custom KPIs */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <ClipboardList className="w-3.5 h-3.5" /> Variabel Custom Target &amp; Realisasi
                  </p>
                  <div className="flex items-center gap-2">
                    <select value={selectedKpiTpl}
                      onChange={(e) => { applyKpiTemplate(e.target.value); setSelectedKpiTpl(""); }}
                      data-testid="kpi-template-select"
                      className="h-7 px-2 rounded-md border border-violet-300 text-[11px] bg-violet-50 min-w-[180px]">
                      <option value="">📋 Load Template Divisi</option>
                      {kpiTemplates.map(t => (
                        <option key={t.key} value={t.key}>{t.is_custom ? "★ " : ""}{t.label} ({t.kpis.length} KPI)</option>
                      ))}
                    </select>
                    <button onClick={addCustomKPI} data-testid="btn-add-kpi"
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 text-[11px] font-medium">
                      <Plus className="w-3 h-3" /> Tambah
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(manualModal.custom_kpis || []).map((k, i) => (
                    <div key={i} className="grid grid-cols-[1fr,110px,110px,auto] gap-2 items-center">
                      <input value={k.name} placeholder="Nama variabel (mis. AKS)"
                        onChange={e => updCustomKPI(i, "name", e.target.value)}
                        data-testid={`kpi-${i}-name`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs" />
                      <input type="number" value={k.target} placeholder="Target"
                        onChange={e => updCustomKPI(i, "target", e.target.value)}
                        data-testid={`kpi-${i}-target`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                      <input type="number" value={k.realisasi} placeholder="Realisasi"
                        onChange={e => updCustomKPI(i, "realisasi", e.target.value)}
                        data-testid={`kpi-${i}-real`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                      <button onClick={() => rmCustomKPI(i)} className="p-1 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {(!manualModal.custom_kpis || manualModal.custom_kpis.length === 0) && (
                    <p className="text-[11px] text-slate-400 italic text-center py-2 border border-dashed border-slate-200 rounded">
                      Belum ada variabel — klik <b>Tambah Variabel</b> untuk menambahkan KPI custom (misal: AKS, Referral Deal, Upsell Rate)
                    </p>
                  )}
                </div>
              </div>

              {/* Kesimpulan / kelemahan */}
              <div className="pt-2 border-t border-slate-100">
                <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Wand2 className="w-3.5 h-3.5 text-emerald-600" /> Coaching Notes
                  </p>
                  {/* Gerbang fitur AI (brief owner 30 Agu 2026) — kunci
                      `ai_assistant`, sama dengan backend /ai/coaching-suggest.
                      Kolom kesimpulan/kelemahan di bawah TETAP bisa diisi
                      manual; yang hilang cuma penyusun otomatisnya. */}
                  {bolehPakaiAI(user, can) && (
                  <button onClick={generateCoachingAI} disabled={aiCoaching}
                    data-testid="btn-ai-coaching"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white text-xs font-medium disabled:opacity-50">
                    <Sparkles className={`w-3.5 h-3.5 ${aiCoaching ? "animate-pulse" : ""}`} />
                    {aiCoaching ? "AI Menyusun..." : "🤖 Susun Coaching AI"}
                  </button>
                  )}
                </div>
                <label className="block mb-3">
                  <span className="text-xs font-medium text-slate-600">Kesimpulan Evaluasi</span>
                  <textarea value={manualModal.kesimpulan}
                    onChange={e => setManualModal(m => ({ ...m, kesimpulan: e.target.value }))}
                    data-testid="perf-kesimpulan"
                    className="mt-1 w-full h-20 p-2 rounded-lg border border-slate-200 text-sm resize-none" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Kelemahan &amp; Rekomendasi Coaching</span>
                  <textarea value={manualModal.kelemahan}
                    onChange={e => setManualModal(m => ({ ...m, kelemahan: e.target.value }))}
                    data-testid="perf-kelemahan"
                    className="mt-1 w-full h-20 p-2 rounded-lg border border-slate-200 text-sm resize-none" />
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={manualGuard.requestClose}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={saveManual} data-testid="btn-save-perf"
                  className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                  Simpan
                </button>
              </div>
              {manualGuard.confirming && (
                <UnsavedGuardBar onStay={manualGuard.cancelClose} onDiscard={manualGuard.confirmDiscard} testid="perf-manual-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Custom KPI Template CRUD Modal (Superadmin only) */}
      {kpiTplModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={kpiTplGuard.requestClose}>
          <div ref={kpiTplGuard.contentRef} className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="kpi-tpl-modal">
            <div className="sticky top-0 bg-gradient-to-r from-violet-700 to-fuchsia-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <LayoutTemplate className="w-6 h-6" />
                <div>
                  <h3 className="font-display font-semibold text-base">Kelola Template KPI Custom</h3>
                  <p className="text-[11px] text-violet-100">
                    {kpiTplModal.mode === "new" ? "Buat template KPI baru" : `Edit: ${kpiTplModal.label}`}
                    {" · "}Tampil di dropdown &ldquo;Load Template Divisi&rdquo; saat isi Catatan Performa
                  </p>
                </div>
              </div>
              <button onClick={kpiTplGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="kpi-tpl-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-0">
              {/* Sidebar */}
              <div className="border-r border-slate-200 bg-slate-50 p-4 max-h-[70vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Template Custom</p>
                  <button onClick={openNewKpiTpl} data-testid="btn-new-kpi-tpl"
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                    <FilePlus2 className="w-3 h-3" /> Baru
                  </button>
                </div>
                {customKpiTemplates.length === 0 && (
                  <p className="text-[11px] text-slate-400 italic text-center py-6">
                    Belum ada template custom.
                  </p>
                )}
                <div className="space-y-1.5">
                  {customKpiTemplates.map(t => (
                    <div key={t.key} data-testid={`kpi-tpl-item-${t.key}`}
                      className={`rounded-lg border p-2.5 cursor-pointer transition-colors ${
                        kpiTplModal._origKey === t.key ? "border-violet-500 bg-white shadow-sm" : "border-slate-200 bg-white hover:border-violet-300"
                      }`}
                      onClick={() => openEditKpiTpl(t)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-slate-900 truncate">{t.label}</p>
                          <p className="text-[10px] text-slate-500 font-mono truncate">{t.key}</p>
                          <p className="text-[10px] text-violet-700 mt-0.5">{(t.kpis || []).length} KPI</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button onClick={(e) => { e.stopPropagation(); openEditKpiTpl(t); }}
                            data-testid={`btn-edit-kpi-tpl-${t.key}`}
                            className="p-1 rounded hover:bg-violet-50 text-slate-500 hover:text-violet-700">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); delKpiTpl(t); }}
                            data-testid={`btn-del-kpi-tpl-${t.key}`}
                            className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <p className="text-[10px] text-slate-500 mb-2">Built-in ({kpiTemplates.length - customKpiTemplates.length})</p>
                  <div className="space-y-1">
                    {kpiTemplates.filter(t => !t.is_custom).map(t => (
                      <div key={t.key} className="rounded-md bg-slate-100 border border-slate-200 p-1.5">
                        <p className="text-[10px] font-medium text-slate-700 truncate">{t.label} · {t.kpis.length} KPI</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Editor */}
              <div className="p-5 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Label Template *</span>
                    <input value={kpiTplModal.label} data-testid="kpi-tpl-label"
                      onChange={e => setKpiTplModal(m => ({ ...m, label: e.target.value }))}
                      placeholder="mis. Marketing Digital"
                      className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600">Key (unique) *</span>
                    <input value={kpiTplModal.key} data-testid="kpi-tpl-key"
                      onChange={e => setKpiTplModal(m => ({ ...m, key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() }))}
                      placeholder="marketing_digital" disabled={kpiTplModal.mode === "edit"}
                      className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-100" />
                  </label>
                </div>

                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <ClipboardList className="w-3.5 h-3.5" /> Daftar KPI ({(kpiTplModal.kpis || []).length})
                  </p>
                  <button onClick={tplAddKpi} data-testid="btn-tpl-add-kpi"
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 text-[11px] font-medium">
                    <Plus className="w-3 h-3" /> Tambah KPI
                  </button>
                </div>

                <div className="space-y-1.5 mb-4">
                  {(kpiTplModal.kpis || []).map((k, i) => (
                    <div key={i} className="grid grid-cols-[1fr,140px,auto] gap-2 items-center">
                      <input value={k.name} placeholder="Nama KPI (mis. AKS, Repeat Rate)"
                        onChange={e => tplUpdKpi(i, "name", e.target.value)}
                        data-testid={`tpl-kpi-${i}-name`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs" />
                      <input type="number" value={k.target} placeholder="Target default"
                        onChange={e => tplUpdKpi(i, "target", e.target.value)}
                        data-testid={`tpl-kpi-${i}-target`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                      <button onClick={() => tplRmKpi(i)}
                        data-testid={`tpl-kpi-${i}-del`}
                        className="p-1 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {(!kpiTplModal.kpis || kpiTplModal.kpis.length === 0) && (
                    <p className="text-[11px] text-slate-400 italic text-center py-3 border border-dashed border-slate-200 rounded">
                      Klik <b>Tambah KPI</b> untuk mulai
                    </p>
                  )}
                </div>

                <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                  <button onClick={kpiTplGuard.requestClose}
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                  <button onClick={saveKpiTpl} disabled={kpiTplSaving}
                    data-testid="btn-save-kpi-tpl"
                    className="h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                    <Save className="w-4 h-4" /> {kpiTplSaving ? "Menyimpan..." : (kpiTplModal.mode === "edit" ? "Perbarui" : "Simpan Template")}
                  </button>
                </div>
              </div>
            </div>
            {kpiTplGuard.confirming && (
              <UnsavedGuardBar onStay={kpiTplGuard.cancelClose} onDiscard={kpiTplGuard.confirmDiscard} testid="kpi-tpl-guard-bar" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SumCard({ icon: Icon, label, value, sub, tint, tid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5" data-testid={tid}>
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-lg md:text-xl font-bold text-slate-900 truncate">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
